// Synthetic-world governed fixture for objective-to-lever evals. Relocated out of the
// canonical decomposer per the routing-literal guard: company names (even synthetic ones)
// live outside canonical source.
import {
  OBJECTIVE_TO_LEVER_DECOMPOSER_SCHEMA_VERSION,
  V1_LOGISTICS_EBITDA_TAXONOMY_STACK_VERSION,
  buildV1LogisticsEbitdaTaxonomyStack,
  type ObjectiveDecomposerFixture,
} from './objective-to-lever-decomposer';

export const NORTHWIND_LOGISTICS_GOVERNED_FIXTURE = {
  fixtureId: 'northwind_logistics_governed_fixture_v1',
  schemaVersion: OBJECTIVE_TO_LEVER_DECOMPOSER_SCHEMA_VERSION,
  profile: {
    tenantId: 'tenant_northwind_logistics',
    profileVersion: 'tenant_business_profile_v1',
    companyName: 'Northwind Logistics',
    industry: 'transportation_logistics',
    businessModel: 'asset_light_logistics_provider',
    revenueScale: {
      value: 2_500_000_000,
      currency: 'USD',
      evidenceRef: 'finance:pnl:fy2026',
    },
    geography: ['US'],
    operatingFootprint: {
      people: { count: 1200, evidenceRef: 'org:snapshot:fy2026' },
      warehouses: { count: 8, evidenceRef: 'network:warehouse:fy2026' },
    },
    financeBaselineRefs: ['finance:pnl:fy2026'],
    orgSnapshotRefs: ['org:snapshot:fy2026'],
    logisticsSnapshotRefs: ['tms:lane-summary:fy2026'],
    connectedSystems: [
      { systemId: 'netsuite_finance', domain: 'finance', status: 'connected' },
      { systemId: 'tms_primary', domain: 'operations', status: 'missing' },
    ],
    knownConstraints: [
      { constraintId: 'cash_budget', label: 'Cash budget', evidenceRef: 'policy:cash' },
    ],
    dataGaps: [
      { gapId: 'carrier_contracts', label: 'Carrier contract terms', missingStateUX: 'connect_X' },
    ],
    evidenceRefs: ['finance:pnl:fy2026', 'org:snapshot:fy2026'],
    asOf: '2026-05-25T00:00:00.000Z',
  },
  taxonomyStack: buildV1LogisticsEbitdaTaxonomyStack({
    resolvedAt: '2026-05-25T00:00:00.000Z',
  }),
  objectiveFrame: {
    schemaVersion: OBJECTIVE_TO_LEVER_DECOMPOSER_SCHEMA_VERSION,
    objective: {
      metric: 'EBITDA',
      direction: 'increase',
      targetPct: 10,
      scope: 'enterprise',
      timeframe: 'FY2026',
    },
    valueGapAnchor: {
      kind: 'evidence_backed',
      metricDefinitionId: 'metric:ebitda:v1',
      baselineEvidenceRef: 'finance:pnl:fy2026',
      baselineValue: 400_000_000,
      targetDelta: 40_000_000,
      targetValue: 440_000_000,
      sourceClass: 'observed',
      sourceBindingRef: 'snapshot:finance:pnl:fy2026',
      asOf: '2026-05-25T00:00:00.000Z',
      blockedIfMissing: false,
    },
    meta: {
      promptPackVersion: 'objective_decomposer_prompt@1.0.0',
      schemaVersion: OBJECTIVE_TO_LEVER_DECOMPOSER_SCHEMA_VERSION,
      taxonomyVersion: V1_LOGISTICS_EBITDA_TAXONOMY_STACK_VERSION,
      taxonomyStack: ['ebitda-core@1.1', 'transportation-logistics@1.0'],
      moduleCatalogVersion: 'module_catalog@1.0.0',
    },
  },
  sourceBindings: [
    {
      evidenceId: 'finance:pnl:fy2026',
      sourceSystem: 'finance_gold_snapshot',
      governedSnapshotId: 'snapshot:finance:pnl:fy2026',
      owner: 'finance',
      asOf: '2026-05-25T00:00:00.000Z',
      sourceClass: 'observed',
      freshness: 'monthly',
      sourceObjectId: 'pnl-fy2026',
      reconciliationRule: 'finance_gold_reconciles_to_board_pack',
    },
  ],
  evidenceReadiness: [
    {
      evidenceId: 'finance:pnl:fy2026',
      present: true,
      sourceClass: 'observed',
      freshnessOk: true,
      asOf: '2026-05-25T00:00:00.000Z',
      status: 'ready',
      missingStateUX: 'not_actionable',
      sourceBindingRef: 'snapshot:finance:pnl:fy2026',
    },
    {
      evidenceId: 'carrier_contracts',
      present: false,
      sourceClass: 'connector-derived',
      freshnessOk: false,
      asOf: null,
      status: 'missing',
      missingStateUX: 'connect_X',
    },
  ],
} as const satisfies ObjectiveDecomposerFixture;
